import React, { useState, useEffect, useCallback } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { styles } from "./styles";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { NewCampaign } from "model/campaign";
import { withMediaQuery, WithMediaQuery } from "component/shared/with-media-query";
import { Dialog } from "component/shared/dialog";
import { DialogTitle } from "component/shared/dialog/title";
import { CampaignForm } from "component/page/campaign/campaign-form";
import { DynamicContentType } from "type/dynamic-content";
import { createCampaign } from "redux/slice/campaigns";
import { getMarketsOptimized, getSortedCampaignDisplayCategories } from "redux/selector";
import * as yup from "yup";
import { validate } from "shared/yup";
import { EmailMarketingType } from "type/email-marketing-type";
import { findDefaultCategory } from "shared/campaign-grouping";
import { CampaignStatus } from "type/campaign-status";
import { isMarketMissing } from "shared/campaign-activation";

interface Props extends
	OwnProps,
	WithMediaQuery,
	WithStyles<typeof styles>
{
}

interface OwnProps {
	open: boolean;
	onClose: () => void;
}

const Component: React.FC<Props> = ({ open, onClose, classes = {} }) => {
	const [submitted, setSubmitted] = useState(false);
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [dynamicContentType, setDynamicContentType] = useState<DynamicContentType | undefined>(DynamicContentType.LEAD_PROFILE);
	const [emailBranding, setEmailBranding] = useState(false);
	const [marketId, setMarketId] = useState<number | undefined>(undefined);
	const [categoryId, setCategoryId] = useState<number | null | undefined>(undefined);
	// MAX-3879: defaults OFF — a new campaign is inactive unless the agent explicitly enables it.
	const [active, setActive] = useState(false);

	const markets = useSelector(getMarketsOptimized);
	const categories = useSelector(getSortedCampaignDisplayCategories);
	const dispatch = useAppThunkDispatch();

	// New campaigns default to the client's "General" category (Q1) — matched by name, not a magic id.
	const defaultCategoryId = findDefaultCategory(categories)?.id;

	const validationSchema = {
		name: yup.string().required("Name is required."),
	};

	// Reset to a clean form whenever the modal is closed, so it opens fresh next time.
	useEffect(() => {
		if (!open) {
			setSubmitted(false);
			setName("");
			setDescription("");
			setDynamicContentType(DynamicContentType.LEAD_PROFILE);
			setEmailBranding(false);
			setMarketId(undefined);
			setCategoryId(defaultCategoryId ?? null);
			setActive(false);
		}
	}, [open, defaultCategoryId]);

	// MAX-4201: choosing Market with no Market selected forces "Set as active" off — the campaign
	// can still be added, but only as inactive.
	const marketMissing = isMarketMissing(dynamicContentType, marketId);
	useEffect(() => {
		if (marketMissing && active) {
			setActive(false);
		}
	}, [marketMissing, active]);

	// Seed the default (General) once categories load, before the user touches the field.
	useEffect(() => {
		if (open && categoryId === undefined) {
			setCategoryId(defaultCategoryId ?? null);
		}
	}, [open, categoryId, defaultCategoryId]);

	const handleSave = useCallback(() => {
		const state = { name, description, dynamicContentType, emailBranding, marketId };
		const errors = validate(validationSchema, state);
		if (!errors) {
			const campaign: NewCampaign = {
				name,
				description,
				emailBrandingYn: emailBranding,
				campaignType: dynamicContentType,
				marketId,
				campaignDisplayCategoryId: categoryId ?? null,
				emailMarketingType: EmailMarketingType.DRIP,
				status: active && !marketMissing ? CampaignStatus.ACTIVE : CampaignStatus.INACTIVE,
			};
			dispatch(createCampaign({ campaign }));
			onClose();
		}
	}, [name, description, dynamicContentType, emailBranding, marketId, categoryId, active, marketMissing, dispatch, onClose, validationSchema]);

	const state = { name, description, dynamicContentType, emailBranding, marketId };
	const errors = validate(validationSchema, state);

	return (
		<Dialog open={open} onClose={() => onClose()} scroll="paper" fullWidth={true} maxWidth="sm">
			<DialogTitle title="Add Campaign" onClose={() => onClose()} />
			<Mui.DialogContent>
				<CampaignForm
					name={name}
					description={description}
					dynamicContentType={dynamicContentType}
					emailBranding={emailBranding}
					marketId={marketId}
					markets={markets}
					categoryId={categoryId}
					categories={categories}
					nameError={submitted && !!errors?.name}
					onNameChange={setName}
					onDescriptionChange={setDescription}
					onDynamicContentTypeChange={setDynamicContentType}
					onEmailBrandingChange={setEmailBranding}
					onMarketIdChange={setMarketId}
					onCategoryChange={setCategoryId}
					active={active}
					onActiveChange={setActive}
				/>
			</Mui.DialogContent>
			<Mui.DialogActions className={classes.actions}>
				<Mui.Button
					variant="outlined"
					color="primary"
					onClick={() => onClose()}
				>
					Cancel
				</Mui.Button>
				<Mui.Button
					variant="contained"
					color="primary"
					onClick={() => {
						setSubmitted(true);
						handleSave();
					}}
				>
					Add Campaign
				</Mui.Button>
			</Mui.DialogActions>
		</Dialog>
	);
};

export const AddCampaign = withMediaQuery("(max-width:600px)")(withStyles(Component, styles));
